// Cue-Editor: Wellenform mit Start, Intro, Post (Outro), Segue und Ende.
// Klick setzt die gewählte Marke, Marken lassen sich ziehen. „Übergang
// anhören“ spielt das Ende dieses Songs und startet am Segue den Folgetitel.

import { useEffect, useRef, useState } from "react";
import { api, fmtMs, toast } from "../../api";

export const MARKS: { key: string; label: string; color: string; hint: string }[] = [
  { key: "cue_in", label: "Start", color: "#2f9a63", hint: "Hier beginnt der Song" },
  { key: "intro", label: "Intro", color: "#2f6fb1", hint: "Ende des Intros (Gesang beginnt)" },
  { key: "outro", label: "Post", color: "#7a4fb3", hint: "Ende des Gesangs" },
  { key: "start_next", label: "Segue", color: "#e8673c", hint: "Hier startet der nächste Titel" },
  { key: "cue_out", label: "Ende", color: "#c8382f", hint: "Hier endet der Song" },
];

function fmtPrecise(ms: number | null | undefined) {
  if (ms == null) return "–";
  const s = ms / 1000;
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toFixed(1).padStart(4, "0")}`;
}

export function CueEditor({ song, onSaved }: { song: any; onSaved: () => void }) {
  const dur = song.duration_ms || 1;
  const [marks, setMarks] = useState<Record<string, number | null>>(() => Object.fromEntries(MARKS.map((m) => [m.key, song[m.key] ?? null])));
  const [active, setActive] = useState("cue_in");
  const [pos, setPos] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [nextQuery, setNextQuery] = useState("");
  const [nextOptions, setNextOptions] = useState<any[]>([]);
  const [nextSong, setNextSong] = useState<any | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const audio = useRef<HTMLAudioElement>(null);
  const nextAudio = useRef<HTMLAudioElement>(null);
  const drag = useRef<string | null>(null);
  const segueTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    setMarks(Object.fromEntries(MARKS.map((m) => [m.key, song[m.key] ?? null])));
  }, [song.id]);

  // Draw waveform and marks.
  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const w = (c.width = c.clientWidth * devicePixelRatio);
    const h = (c.height = 140 * devicePixelRatio);
    const g = c.getContext("2d")!;
    g.clearRect(0, 0, w, h);
    const wave: number[] = song.waveform ?? [];
    const x = (ms: number) => (ms / dur) * w;
    // Played-out regions (before start, after end) dimmed.
    g.fillStyle = "#f1eee6";
    g.fillRect(0, 0, x(marks.cue_in ?? 0), h);
    g.fillRect(x(marks.cue_out ?? dur), 0, w, h);
    // Hook (from mAirList: HookIn … HookOut) as a shaded band.
    const hook = song.custom?.marks_extra;
    if (hook?.hook_in != null && hook?.hook_out != null) {
      g.fillStyle = "rgba(122, 79, 179, 0.12)";
      g.fillRect(x(hook.hook_in), 0, x(hook.hook_out) - x(hook.hook_in), h);
      g.fillStyle = "#7a4fb3";
      g.font = `${10 * devicePixelRatio}px sans-serif`;
      g.fillText("Hook", x(hook.hook_in) + 3 * devicePixelRatio, h - 6 * devicePixelRatio);
    }
    g.fillStyle = "#1a372d";
    const n = wave.length || 1;
    for (let i = 0; i < wave.length; i++) {
      const amp = wave[i] * (h / 2 - 4);
      g.fillRect((i / n) * w, h / 2 - amp, Math.max(1, w / n - 0.5), amp * 2 || 1);
    }
    if (!wave.length) {
      g.fillStyle = "#7c8a84";
      g.font = `${12 * devicePixelRatio}px sans-serif`;
      g.fillText("Keine Wellenform – „Neu analysieren“ erzeugt sie", 10 * devicePixelRatio, h / 2);
    }
    for (const m of MARKS) {
      const v = marks[m.key];
      if (v == null) continue;
      g.fillStyle = m.color;
      g.fillRect(x(v) - devicePixelRatio, 0, 2 * devicePixelRatio, h);
      g.font = `bold ${10 * devicePixelRatio}px sans-serif`;
      g.fillText(m.label, Math.min(x(v) + 3 * devicePixelRatio, w - 40 * devicePixelRatio), (12 + MARKS.indexOf(m) * 12) * devicePixelRatio);
    }
    g.fillStyle = "#18231f";
    g.fillRect(x(pos) - devicePixelRatio / 2, 0, devicePixelRatio, h);
  }, [marks, pos, song.waveform, dur]);

  useEffect(() => {
    const a = audio.current;
    if (!a) return;
    const t = () => setPos(a.currentTime * 1000);
    const end = () => setPlaying(false);
    a.addEventListener("timeupdate", t);
    a.addEventListener("pause", end);
    return () => {
      a.removeEventListener("timeupdate", t);
      a.removeEventListener("pause", end);
    };
  }, []);

  useEffect(() => () => window.clearTimeout(segueTimer.current), []);

  const msAt = (e: React.MouseEvent) => {
    const r = (e.target as HTMLCanvasElement).getBoundingClientRect();
    return Math.round(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * dur);
  };
  const nearMark = (ms: number) => {
    const tol = dur * 0.01;
    return MARKS.find((m) => marks[m.key] != null && Math.abs((marks[m.key] as number) - ms) < tol)?.key ?? null;
  };

  const playFrom = (ms: number) => {
    const a = audio.current;
    if (!a) return;
    stopAll();
    a.currentTime = ms / 1000;
    a.play().then(() => setPlaying(true)).catch(() => toast("Wiedergabe nicht möglich", true));
  };
  const stopAll = () => {
    window.clearTimeout(segueTimer.current);
    audio.current?.pause();
    nextAudio.current?.pause();
    setPlaying(false);
  };

  const searchNext = async (q: string) => {
    setNextQuery(q);
    if (q.trim().length < 2) return setNextOptions([]);
    const r = await api(`/api/library?q=${encodeURIComponent(q)}&view=all`);
    setNextOptions(r.items.slice(0, 8));
  };

  // End of this song from 6 s before the segue; at the segue the next song starts at its own start.
  const transition = () => {
    const seg = marks.start_next ?? marks.cue_out ?? dur;
    const from = Math.max(marks.cue_in ?? 0, seg - 6000);
    playFrom(from);
    if (!nextSong || !nextAudio.current) return;
    const n = nextAudio.current;
    n.src = `/api/media/${nextSong.id}/audio`;
    n.currentTime = (nextSong.cue_in ?? 0) / 1000;
    segueTimer.current = window.setTimeout(() => {
      n.currentTime = (nextSong.cue_in ?? 0) / 1000;
      n.play().catch(() => {});
    }, seg - from);
    // This song stops at its end.
    const endIn = (marks.cue_out ?? dur) - from;
    window.setTimeout(() => audio.current?.pause(), endIn);
  };

  const save = async () => {
    const end = marks.cue_out ?? dur;
    const seg = marks.start_next;
    if (end < dur / 2) return toast("Das Ende liegt in der ersten Hälfte – so kann das nicht stimmen.", true);
    if (seg != null && end - seg > 10_000 && !window.confirm(`Zwischen Segue und Ende liegen ${Math.round((end - seg) / 1000)} s Ausklang. Ist das so gewollt?`)) return;
    try {
      await api(`/api/media/${song.id}`, { method: "PUT", body: marks });
      toast("Cues gespeichert");
      onSaved();
    } catch (e: any) {
      toast(e.message, true);
    }
  };

  return (
    <div className="stack">
      <div className="row wrap">
        {MARKS.map((m) => (
          <button key={m.key} className="btn small" title={m.hint} onClick={() => setActive(m.key)}
            style={{ borderColor: active === m.key ? m.color : undefined, boxShadow: active === m.key ? `inset 0 -3px 0 ${m.color}` : undefined }}>
            <span style={{ color: m.color }}>●</span> {m.label} <span className="muted">{fmtPrecise(marks[m.key])}</span>
          </button>
        ))}
      </div>
      <canvas
        ref={canvas}
        style={{ width: "100%", height: 140, borderRadius: 8, border: "1px solid var(--line)", cursor: "crosshair", background: "#fbfaf6" }}
        onMouseDown={(e) => {
          const ms = msAt(e);
          const near = nearMark(ms);
          if (near) {
            drag.current = near;
            setActive(near);
          } else {
            setMarks({ ...marks, [active]: ms });
          }
        }}
        onMouseMove={(e) => {
          if (drag.current) setMarks((m) => ({ ...m, [drag.current as string]: msAt(e) }));
        }}
        onMouseUp={() => (drag.current = null)}
        onMouseLeave={() => (drag.current = null)}
        onDoubleClick={(e) => playFrom(msAt(e))}
      />
      <div className="small muted">Klick setzt die gewählte Marke („{MARKS.find((m) => m.key === active)?.label}“), Marken lassen sich ziehen, Doppelklick spielt ab der Stelle. Position {fmtPrecise(pos)} / {fmtMs(dur)}</div>
      <audio ref={audio} src={`/api/media/${song.id}/audio`} preload="auto" />
      <audio ref={nextAudio} preload="none" />
      <div className="row wrap">
        {playing ? <button className="btn" onClick={stopAll}>■ Stopp</button> : <button className="btn" onClick={() => playFrom(marks[active] ?? 0)}>▶ ab {MARKS.find((m) => m.key === active)?.label}</button>}
        <button className="btn" onClick={() => setMarks({ ...marks, [active]: Math.round(pos) })} title="Gewählte Marke auf die Abspielposition setzen">⌖ Marke hier</button>
        <button className="btn" onClick={() => setMarks({ ...marks, [active]: null })} disabled={active === "cue_in" || active === "cue_out"}>Marke entfernen</button>
        <span className="spacer" />
        <button className="btn primary" onClick={save}>Cues speichern</button>
      </div>
      <div className="card" style={{ padding: 12, background: "#fbfaf6" }}>
        <h3>Übergang anhören</h3>
        <div className="row wrap">
          <input type="text" style={{ maxWidth: 280 }} placeholder="Folgetitel suchen …" value={nextQuery} onChange={(e) => searchNext(e.target.value)} />
          {nextSong && <span className="badge accent">danach: {nextSong.artist ? `${nextSong.artist} – ` : ""}{nextSong.title}</span>}
          <button className="btn" onClick={transition}>▶ Übergang {nextSong ? "mit Folgetitel" : "(nur Ausklang)"}</button>
        </div>
        {nextOptions.length > 0 && !nextSong && (
          <div style={{ marginTop: 6 }}>
            {nextOptions.map((o) => (
              <div key={o.id} className="small" style={{ cursor: "pointer", padding: "3px 0" }} onClick={() => { setNextSong(o); setNextOptions([]); }}>
                {o.artist ? `${o.artist} – ` : ""}{o.title} <span className="muted">{fmtMs(o.duration_ms)}</span>
              </div>
            ))}
          </div>
        )}
        {nextSong && <button className="btn small" style={{ marginTop: 6 }} onClick={() => { setNextSong(null); setNextQuery(""); }}>Folgetitel ändern</button>}
      </div>
    </div>
  );
}
